<script setup lang="ts">
import { URL_IMAGE } from '@/config/constants'
import { getImageClassRounded } from '@/happyTheme/utils/catalogCategory/getImageClassRounded'
import { checkTruncationHappy } from '@/happyTheme/utils/product/checkTrunctationHappy'
import type { Product } from '@/storage/products'
import { convertPriceToEuro } from '@/utils/price/convertPrice'
import { formatPrice } from '@/event/locale'
import { getProductName } from '@/utils/product/getProductName'
import { nextTick, onMounted, ref } from 'vue'

const props = defineProps<{
    product: Product
    description: string
}>()

const imageSizes = ref<Record<number, { width: number; height: number }>>({})

const maxLines = ref<number>(1)
const descriptionElement = ref<HTMLElement>()
const divDescriptionElement = ref<HTMLElement>()
const buttonElementSelect = ref<HTMLElement>()

onMounted(() => {
    nextTick(() => {
        checkTruncationHappy(
            descriptionElement,
            divDescriptionElement,
            buttonElementSelect,
            maxLines
        )
        window.addEventListener('resize', () =>
            checkTruncationHappy(
                descriptionElement,
                divDescriptionElement,
                buttonElementSelect,
                maxLines
            )
        )
    })
})

function onImageLoad(categoryId: number, event: Event) {
    const img = event.target as HTMLImageElement
    imageSizes.value[categoryId] = { width: img.naturalWidth, height: img.naturalHeight }
}
</script>

<template>
    <section class="section-parent-card-empty-product-happy">
        <section class="section-card-product-empty-happy">
            <div class="div-card-product-empty-happy">
                <img
                    class="img-card-product-empty-happy"
                    :class="{
                        'object-contain': props.product.hasCleanImg,
                        'object-cover object-center': !props.product.hasCleanImg
                    }"
                    :src="`${URL_IMAGE}${props.product.image}`"
                    :alt="getProductName(props.product)"
                    @load="onImageLoad(props.product.product_id, $event)"
                />
            </div>

            <div
                class="div-description-card-product-empty-happy"
                ref="divDescriptionElement"
            >
                <div class="div-description-parent-card-product-empty-happy">
                    <div
                        ref="descriptionElement"
                        class="div-description-child-card-product-empty-happy"
                    >
                        <h3 class="text-product-name-empty-happy">
                            {{ getProductName(props.product) }}
                        </h3>
                        <p class="text-price-product-empty-happy">
                            {{ formatPrice(props.product.price) }}
                        </p>
                    </div>
                    <p
                        v-if="props.description !== 'Aucune description' && maxLines > 0"
                        :class="`line-clamp-${maxLines}`"
                        :style="{ '-webkit-line-clamp': maxLines }"
                        class="text-description-product-empty-happy"
                    >
                        {{ props.description }}
                    </p>
                </div>

                <div
                    class="div-button-select-product-empty-happy"
                    ref="buttonElementSelect"
                >
                    {{ $t('select') }}
                </div>
            </div>
        </section>
    </section>
</template>

<style scoped>
.section-parent-card-empty-product-happy {
    @apply w-full h-full flex justify-center items-center p-0.5;
}

.section-card-product-empty-happy {
    @apply flex flex-col justify-start items-center min-h-52 w-full h-full rounded-lg overflow-hidden shadow-sm;
    /* refactored */
    @apply shadow-t-accent;
    @apply happy-ifo-dark:shadow-t-accent/10 happy-ifo-dark:bg-t-card/90 happy-ifo-dark:border happy-ifo-dark:border-t-accent/15 happy-ifo-dark:shadow-lg;
}

.div-card-product-empty-happy {
    @apply w-full flex justify-center items-center cursor-pointer ;
}

.img-card-product-empty-happy {
    @apply h-36 w-full;
}

.div-description-card-product-empty-happy {
    @apply w-full h-full flex flex-col justify-between items-start p-2;
}

.div-description-parent-card-product-empty-happy {
    @apply w-full flex flex-col justify-start;
}

.div-description-child-card-product-empty-happy {
    @apply w-full flex justify-between items-start;
    @apply flex-shrink-0;
    /* refactored */
    @apply text-t-text;
}
.text-product-name-empty-happy {
    @apply text-sm font-semibold text-wrap max-w-40;
}

.text-price-product-empty-happy {
    /* refactored */
    @apply text-sm font-bold text-nowrap text-t-accent pl-2;
    @apply happy-ifo-dark:text-t-accent;
}

.text-description-product-empty-happy {
    @apply text-xs text-ellipsis overflow-hidden mt-1;
    /* refactored */
    @apply text-t-text;
    display: -webkit-box;
    -webkit-box-orient: vertical;
}

.div-button-select-product-empty-happy {
    @apply w-full h-8  text-center text-sm font-bold flex justify-center items-center rounded-md mt-2 cursor-pointer shadow-sm;
    /* refactored */
    @apply bg-t-accent text-t-on-accent shadow-t-border;
    /* refactored: ifo-dark/light pill style — dark-transparan bg + amber
       border + amber text. Ekle butonunun dark-pill tarzına uyumlu. */
    @apply happy-ifo-dark:text-t-accent
           happy-ifo-dark:bg-t-accent/10
           happy-ifo-dark:border
           happy-ifo-dark:border-t-accent/30
           happy-ifo-dark:shadow-none
           happy-ifo-dark:rounded-full
           happy-ifo-dark:tracking-wide;
}

.section-card-product-empty-happy:active,
.section-card-product-empty-happy:focus {
    /* refactored */
    @apply bg-t-accent-hover;
    @apply happy-ifo-dark:bg-t-accent-hover/20 happy-ifo-dark:shadow-[0_0_16px_rgba(212,160,23,0.5)] happy-ifo-dark:border-t-accent/50;
}

.section-card-product-empty-happy:active
    .div-description-card-product-empty-happy
    .div-description-child-card-product-empty-happy
    .text-product-name-empty-happy,
.section-card-product-empty-happy:active
    .div-description-card-product-empty-happy
    .text-description-product-empty-happy {
    /* refactored */
    @apply text-t-on-accent;
    @apply happy-ifo-dark:text-t-accent;
}

.section-card-product-empty-happy:active .div-button-select-product-empty-happy,
.section-card-product-empty-happy:focus .div-button-select-product-empty-happy {
    /* refactored */
    @apply bg-t-accent text-t-on-accent;
    /* refactored: ifo-dark pill'i korusun — solid amber'a dönmesin; hafif
       brightness + belirgin border ile active state hissi versin. */
    @apply happy-ifo-dark:bg-t-accent/20
           happy-ifo-dark:text-t-accent
           happy-ifo-dark:border-t-accent/60;
}

.section-card-product-empty-happy:active .text-price-product-empty-happy,
.section-card-product-empty-happy:focus .text-price-product-empty-happy {
    /* refactored */
    @apply text-t-on-accent;
    @apply happy-ifo-dark:text-t-accent;
}

@media (hover: hover) {
    .section-card-product-empty-happy:hover {
        /* refactored */
        @apply bg-t-accent-hover;
        @apply happy-ifo-dark:bg-t-accent-hover/20 happy-ifo-dark:shadow-[0_0_16px_rgba(212,160,23,0.5)] happy-ifo-dark:border-t-accent/50;
    }

    .section-card-product-empty-happy:hover
        .div-description-card-product-empty-happy
        .div-description-child-card-product-empty-happy
        .text-product-name-empty-happy,
    .section-card-product-empty-happy:hover
        .div-description-card-product-empty-happy
        .text-description-product-empty-happy {
        /* refactored */
        @apply text-t-on-accent;
    @apply happy-ifo-dark:text-t-accent;
    }

    .section-card-product-empty-happy:hover .div-button-select-product-empty-happy {
        /* refactored */
        @apply bg-t-accent text-t-on-accent;
        /* refactored: ifo-dark pill'i korusun — hover'da parlak pill hissi. */
        @apply happy-ifo-dark:bg-t-accent/20
               happy-ifo-dark:text-t-accent
               happy-ifo-dark:border-t-accent/60;
    }

    .section-card-product-empty-happy:hover .text-price-product-empty-happy {
        /* refactored */
        @apply text-t-on-accent;
    @apply happy-ifo-dark:text-t-accent;
    }
}
</style>
